import {
  Location__City,
  Location__Service,
  Location__State,
} from '@graphql/platform';

import { getHomeLocationCityLabel } from './location_display_names';
import { resolveCityRegionPageUrl } from './region_page_url';

type RegionPage = {
  slug: string;
  title: string;
};

export type HomeLocationState = Pick<
  Location__State,
  'id' | 'code' | 'name' | 'slug'
> & {
  cities: ReadonlyArray<
    Pick<Location__City, 'id' | 'slug' | 'name' | 'services'>
  >;
};

type HomeLocationColumnCity = Pick<Location__City, 'id' | 'slug' | 'name'> & {
  label: string;
  href: string;
};

export type HomeLocationColumnGroup = {
  state: Pick<Location__State, 'id' | 'name' | 'slug'>;
  cities: HomeLocationColumnCity[];
};

type HomeLocationColumnGroupOrder = {
  stateSlug: string;
  citySlugs: ReadonlyArray<string>;
};

/** Column-major footer layout from Figma — slugs only; labels come from the DB. */
const HOME_LOCATION_COLUMN_ORDER: ReadonlyArray<
  ReadonlyArray<HomeLocationColumnGroupOrder>
> = [
  [
    { stateSlug: 'arizona', citySlugs: ['phoenix'] },
    {
      stateSlug: 'california',
      citySlugs: [
        'bay-area',
        'fresno',
        'inland-empire',
        'los-angeles',
        'orange-county',
        'san-diego',
      ],
    },
    { stateSlug: 'colorado', citySlugs: ['denver'] },
    { stateSlug: 'district-of-columbia', citySlugs: ['washington-dc'] },
  ],
  [
    {
      stateSlug: 'florida',
      citySlugs: ['jacksonville', 'miami', 'orlando', 'tampa-bay'],
    },
    { stateSlug: 'illinois', citySlugs: ['chicago'] },
    { stateSlug: 'maryland', citySlugs: ['baltimore'] },
    { stateSlug: 'massachusetts', citySlugs: ['boston'] },
    { stateSlug: 'michigan', citySlugs: ['detroit'] },
  ],
  [
    { stateSlug: 'new-jersey', citySlugs: ['new-jersey'] },
    {
      stateSlug: 'new-york',
      citySlugs: ['the-bronx', 'brooklyn', 'manhattan', 'new-york', 'queens'],
    },
    { stateSlug: 'ontario', citySlugs: ['toronto'] },
    {
      stateSlug: 'pennsylvania',
      citySlugs: ['philadelphia', 'pittsburgh'],
    },
  ],
  [
    { stateSlug: 'tennessee', citySlugs: ['nashville'] },
    {
      stateSlug: 'texas',
      citySlugs: ['austin', 'dallas', 'houston', 'san-antonio'],
    },
    {
      stateSlug: 'virginia',
      citySlugs: ['alexandria', 'arlington'],
    },
    { stateSlug: 'washington', citySlugs: ['seattle'] },
  ],
];

export const buildHomeLocationColumns = (
  states: ReadonlyArray<HomeLocationState>,
  pages: ReadonlyArray<RegionPage>,
  service: Location__Service = Location__Service.SmartStorage,
): HomeLocationColumnGroup[][] => {
  const statesBySlug = new Map(states.map((state) => [state.slug, state]));

  return HOME_LOCATION_COLUMN_ORDER.map((column) =>
    column
      .map((groupOrder) => {
        const state = statesBySlug.get(groupOrder.stateSlug);
        if (!state) return null;

        const cities = groupOrder.citySlugs
          .map((citySlug) =>
            state.cities.find((city) => city.slug === citySlug),
          )
          .filter(
            (city): city is HomeLocationState['cities'][number] =>
              !!city && city.services.includes(service),
          )
          .map((city) => ({
            id: city.id,
            slug: city.slug,
            name: city.name,
            label: getHomeLocationCityLabel(city.slug, city.name),
            href: resolveCityRegionPageUrl({
              cityName: city.name,
              citySlug: city.slug,
              stateSlug: state.slug,
              pages,
            }),
          }));

        if (!cities.length) return null;

        return {
          state: {
            id: state.id,
            name: state.name,
            slug: state.slug,
          },
          cities,
        };
      })
      .filter((group): group is HomeLocationColumnGroup => group !== null),
  );
};

export const countHomeLocationGroups = (
  columns: ReadonlyArray<ReadonlyArray<HomeLocationColumnGroup>>,
) => columns.reduce((total, column) => total + column.length, 0);
